/**
 * Anuncios (27/10, Gabor: desde el Panel «me lleva a todos los anuncios»): lista por estado o solo los
 * destacados, los más recientes primero, con enlace a la ficha pública y al vendedor.
 * `?filtro=activos|destacados|pausados|vendidos|revision`.
 */
import { useEffect, useState, type ReactElement } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { collection, getDocs, limit, orderBy, query, where } from 'firebase/firestore';
import { productUrl } from '@nuvvu/shared';
import { errorText } from '../api.js';
import { env } from '../env.js';
import { db } from '../firebase.js';
import { btn, muted, Notice, str, Table, td } from '../lib/ui.js';

type Doc = Record<string, unknown>;
const FILTERS = {
  activos: 'Activos',
  destacados: 'Destacados',
  pausados: 'Pausados',
  vendidos: 'Vendidos',
  revision: 'En revisión',
} as const;
type Filter = keyof typeof FILTERS;
const STATUS: Record<Exclude<Filter, 'destacados'>, string> = {
  activos: 'active',
  pausados: 'paused',
  vendidos: 'sold',
  revision: 'pending_review',
};

const price = (p: Doc): string => {
  const v = typeof p.price === 'number' ? p.price : 0;
  return `${v.toLocaleString('es-ES')} ${str(p.currency)}`;
};

export function ListingsPage(): ReactElement {
  const [params, setParams] = useSearchParams();
  const raw = params.get('filtro') ?? 'activos';
  const filtro: Filter = raw in FILTERS ? (raw as Filter) : 'activos';
  const [rows, setRows] = useState<Doc[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    setRows(null);
    setError(null);
    const base = collection(db, 'products');
    const q =
      filtro === 'destacados'
        ? query(base, where('status', '==', 'active'), where('is_featured', '==', true), limit(300))
        : query(
            base,
            where('status', '==', STATUS[filtro]),
            orderBy('created_date', 'desc'),
            limit(200),
          );
    getDocs(q)
      .then((s) => {
        setRows(s.docs.map((d) => ({ id: d.id, ...d.data() })));
      })
      .catch((e: unknown) => {
        setError(errorText(e));
        setRows([]);
      });
  }, [filtro]);
  return (
    <>
      <h1 style={{ margin: 0 }}>Anuncios</h1>
      <p style={muted}>
        Los 200 más recientes de cada estado (destacados: hasta 300). Moderar: Moderación.
        Promociones pagadas: Monetización.
      </p>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        {(Object.keys(FILTERS) as Filter[]).map((f) => (
          <button
            key={f}
            type="button"
            style={btn(filtro === f)}
            onClick={() => {
              setParams({ filtro: f }, { replace: true });
            }}
          >
            {FILTERS[f]}
          </button>
        ))}
      </div>
      <Notice text={error} error />
      {!rows && <p style={muted}>Cargando…</p>}
      {rows && (
        <Table
          head={[
            'Anuncio',
            'Precio',
            'País',
            'Vendedor',
            filtro === 'destacados' ? 'Destacado hasta' : 'Publicado',
          ]}
        >
          {rows.map((p) => (
            <tr key={str(p.id)}>
              <td style={td}>
                <a
                  href={`${env.VITE_WEB_ORIGIN}${productUrl('es', {
                    id: str(p.id),
                    title: str(p.title),
                  })}`}
                  target="_blank"
                  rel="noreferrer"
                >
                  {str(p.title) || str(p.id)}
                </a>
              </td>
              <td style={td}>{price(p)}</td>
              <td style={td}>{str(p.country_code) || '—'}</td>
              <td style={td}>
                <Link to={`/usuarios/${str(p.seller_id)}`}>
                  {str(p.seller_name) || str(p.seller_id)}
                </Link>
              </td>
              <td style={td}>
                {filtro === 'destacados'
                  ? str(p.featured_until).slice(0, 10) || 'sin caducidad (PRO 1.0)'
                  : str(p.created_date).slice(0, 10)}
              </td>
            </tr>
          ))}
          {rows.length === 0 && (
            <tr>
              <td style={td} colSpan={5}>
                Ninguno.
              </td>
            </tr>
          )}
        </Table>
      )}
    </>
  );
}
